import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles, HeartPulse, Salad, Leaf } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { CTASection } from "@/components/site/CTASection";
import { IMAGES, STAGE_IMAGES } from "@/lib/assets";

export default function Home() {
  const { t } = useTranslation();
  const whyPoints = t("home.whyPoints", { returnObjects: true }) || [];
  const categories = [
    { key: "hormonalHealth", base: "/hormonal-health", icon: Sparkles },
    { key: "fertilityPregnancy", base: "/fertility-pregnancy", icon: HeartPulse },
    { key: "menopauseAging", base: "/menopause-aging", icon: Leaf },
    { key: "preventiveMedicine", base: "/preventive-medicine", icon: Salad },
  ].map((c) => ({
    ...c,
    kicker: t(`${c.key}.hubKicker`),
    title: t(`${c.key}.hubTitle`),
    text: t(`${c.key}.hubText`),
    items: t(`${c.key}.items`, { returnObjects: true }) || [],
  }));

  return (
    <div data-testid="home-page">
      {/* MISSION BANNER */}
      <div data-testid="home-mission-banner" className="border-b border-champagne-gold/25 bg-deep-ink">
        <p className="mx-auto max-w-4xl px-5 py-4 text-center font-serif text-base italic leading-relaxed text-warm-ivory/90 lg:text-lg">
          {t("home.missionBanner")}
        </p>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden bg-warm-ivory">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 pb-16 pt-14 lg:grid-cols-[1.05fr_1fr] lg:gap-16 lg:px-10 lg:pb-24 lg:pt-20">
          <div>
            <Reveal>
              <p className="mb-6 text-sm font-medium uppercase tracking-editorial text-champagne-gold">
                {t("home.heroKicker")}
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="text-balance font-serif text-[2.75rem] leading-[1.02] tracking-tight text-deep-ink sm:text-6xl lg:text-7xl">
                {t("home.heroTitle")}
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 font-serif text-2xl italic text-deep-ink/70 lg:text-3xl">
                {t("home.heroSubtitle")}
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-deep-ink/70">
                {t("home.heroText")}
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/book"
                  data-testid="hero-book-button"
                  className="group inline-flex items-center gap-2 rounded-full bg-deep-ink px-7 py-3.5 font-medium text-warm-ivory transition-transform hover:scale-[1.02]"
                >
                  {t("home.heroCta")}
                  <ArrowRight className="rtl-flip h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Link>
                <a
                  href="#categories"
                  data-testid="hero-explore-button"
                  className="inline-flex items-center gap-2 rounded-full border border-deep-ink/20 px-7 py-3.5 font-medium text-deep-ink transition-colors hover:border-champagne-gold hover:text-champagne-gold"
                >
                  {t("home.heroCtaSecondary")}
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem]">
                <img src={IMAGES.hero} alt="" className="h-full w-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -start-6 hidden rounded-2xl bg-warm-ivory px-6 py-5 shadow-[0_20px_50px_rgba(26,37,48,0.12)] sm:block">
                <p className="font-arabic text-3xl text-champagne-gold" dir="rtl">حياة</p>
                <p className="mt-1 text-xs uppercase tracking-editorial text-deep-ink/60">
                  {t("meta.tagline")}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CATEGORIES */}
      <section id="categories" data-testid="home-categories" className="bg-sand">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
          <div className="max-w-2xl">
            <Reveal>
              <p className="mb-4 text-sm font-medium uppercase tracking-editorial text-champagne-gold">
                {t("home.categoriesKicker")}
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl leading-tight text-deep-ink lg:text-5xl">
                {t("home.categoriesTitle")}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-lg leading-relaxed text-deep-ink/70">{t("home.categoriesText")}</p>
            </Reveal>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {categories.map((c, i) => (
              <Reveal key={c.key} delay={(i % 2) * 0.06}>
                <Link
                  to={c.base}
                  data-testid={`home-category-${c.key}`}
                  className="group flex h-full flex-col rounded-3xl border border-border bg-warm-ivory p-8 transition-colors hover:border-champagne-gold"
                >
                  <div className="mb-4 flex items-center gap-2 text-champagne-gold">
                    <c.icon className="h-5 w-5" strokeWidth={1.6} />
                    <span className="text-sm font-medium uppercase tracking-editorial">{c.kicker}</span>
                  </div>
                  <h3 className="font-serif text-2xl leading-snug text-deep-ink">{c.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-deep-ink/70">{c.text}</p>
                  <ul className="mt-5 flex flex-wrap gap-2">
                    {c.items.slice(0, 4).map((it) => (
                      <li key={it.slug} className="rounded-full bg-sand px-3 py-1 text-xs text-deep-ink/70">{it.title}</li>
                    ))}
                  </ul>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-champagne-gold">
                    {t("common.learnMore")}
                    <ArrowRight className="rtl-flip h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LONGEVITY + NUTRITION split */}
      <section className="bg-warm-ivory">
        <div className="mx-auto grid max-w-7xl gap-6 px-5 py-20 lg:grid-cols-2 lg:px-10 lg:py-28">
          {[
            { img: IMAGES.longevity, kicker: t("home.longevityKicker"), title: t("home.longevityTitle"), text: t("home.longevityText"), to: "/menopause-aging/longevity", icon: HeartPulse },
            { img: IMAGES.nutrition, kicker: t("home.nutritionKicker"), title: t("home.nutritionTitle"), text: t("home.nutritionText"), to: "/preventive-medicine/ernaehrung", icon: Salad },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <Link to={c.to} className="group flex h-full flex-col overflow-hidden rounded-[2rem] bg-warm-ivory">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={c.img} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="flex flex-1 flex-col p-8 lg:p-10">
                  <div className="mb-4 flex items-center gap-2 text-champagne-gold">
                    <c.icon className="h-5 w-5" strokeWidth={1.6} />
                    <span className="text-sm font-medium uppercase tracking-editorial">{c.kicker}</span>
                  </div>
                  <h3 className="font-serif text-3xl leading-tight text-deep-ink">{c.title}</h3>
                  <p className="mt-4 flex-1 text-base leading-relaxed text-deep-ink/70">{c.text}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-champagne-gold">
                    {t("common.learnMore")}
                    <ArrowRight className="rtl-flip h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY HAYAT */}
      <section data-testid="home-why" className="bg-warm-ivory">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-28">
          <div className="max-w-2xl">
            <Reveal>
              <p className="mb-4 flex items-center gap-2 text-sm font-medium uppercase tracking-editorial text-champagne-gold">
                <Sparkles className="h-4 w-4" /> {t("home.whyKicker")}
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl leading-tight text-deep-ink lg:text-5xl">{t("home.whyTitle")}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-lg leading-relaxed text-deep-ink/70">{t("home.whyText")}</p>
            </Reveal>
          </div>
          <div className="mt-12 grid gap-x-10 gap-y-10 sm:grid-cols-2">
            {whyPoints.map((p, i) => (
              <Reveal key={i} delay={(i % 2) * 0.06}>
                <div className="border-t border-border pt-6">
                  <div className="mb-3 flex items-center gap-3">
                    <span className="font-serif text-xl text-champagne-gold">{String(i + 1).padStart(2, "0")}</span>
                    <Leaf className="h-4 w-4 text-muted-sage" />
                  </div>
                  <h3 className="font-serif text-2xl text-deep-ink">{p.title}</h3>
                  <p className="mt-2 text-base leading-relaxed text-deep-ink/70">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MEET THE DOCTOR */}
      <section data-testid="home-doctor" className="bg-sand">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-20 lg:grid-cols-[1fr_1.15fr] lg:gap-16 lg:px-10 lg:py-28">
          <Reveal>
            <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem]">
              <img src={IMAGES.doctor} alt="" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="mb-4 text-sm font-medium uppercase tracking-editorial text-champagne-gold">
                {t("home.doctorKicker")}
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl leading-tight text-deep-ink lg:text-5xl">{t("home.doctorTitle")}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-lg leading-relaxed text-deep-ink/70">{t("home.doctorText")}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/about"
                data-testid="home-doctor-link"
                className="group mt-8 inline-flex items-center gap-2 rounded-full border border-deep-ink/20 px-7 py-3.5 font-medium text-deep-ink transition-colors hover:border-champagne-gold hover:text-champagne-gold"
              >
                {t("home.doctorCta")}
                <ArrowRight className="rtl-flip h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
